import React, { useMemo, useState } from "react";
import type { Permission, PermissionGroup } from "../types";

interface PermissionItemProps {
  permission: Permission;
  isChecked: boolean;
  onToggle: () => void;
}

const PermissionItem: React.FC<PermissionItemProps> = ({ permission, isChecked, onToggle }) => (
  <label
    htmlFor={`perm-${permission.id}`}
    className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
      isChecked
        ? "bg-emerald-50 border-emerald-200"
        : "bg-white border-gray-100 hover:border-gray-200 hover:bg-gray-50"
    }`}
  >
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
      isChecked ? "bg-emerald-500" : "bg-gray-100"
    }`}>
      <svg className={`w-5 h-5 ${isChecked ? "text-white" : "text-gray-500"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    </div>
    <div className="flex-1 min-w-0">
      <h4 className={`font-semibold ${isChecked ? "text-emerald-700" : "text-gray-900"}`}>
        {permission.name}
      </h4>
      <p className="text-sm text-gray-500 mt-0.5">{permission.description || "Không có mô tả"}</p>
      <span className="inline-block mt-2 px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full font-mono">
        {permission.code}
      </span>
    </div>
    <div className="flex items-center h-10">
      <input
        type="checkbox"
        id={`perm-${permission.id}`}
        checked={isChecked}
        onChange={onToggle}
        className="h-5 w-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
      />
    </div>
  </label>
);

// Loading skeleton
const PermissionSkeleton = () => (
  <div className="space-y-4 p-6">
    {[1, 2, 3].map((g) => (
      <div key={g} className="rounded-xl border border-gray-200 bg-white animate-pulse">
        <div className="p-4 border-b border-gray-100">
          <div className="h-5 bg-gray-200 rounded w-1/4" />
        </div>
        <div className="p-4 space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-start gap-4 p-4 rounded-xl bg-gray-50">
              <div className="w-10 h-10 bg-gray-200 rounded-xl" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/2" />
                <div className="h-3 bg-gray-200 rounded w-3/4" />
              </div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

interface PermissionListPanelProps {
  permissions: Permission[];
  selectedItemName: string | null | undefined;
  stagedPermissions: Set<number>;
  isLoadingAll: boolean;
  isLoadingItem: boolean;
  isSaving: boolean;
  isDirty: boolean;
  onTogglePermission: (id: number) => void;
  onSave: () => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
}

export const PermissionListPanel: React.FC<PermissionListPanelProps> = ({
  permissions,
  selectedItemName,
  stagedPermissions,
  isLoadingAll,
  isLoadingItem,
  isSaving,
  isDirty,
  onTogglePermission,
  onSave,
  searchTerm,
  onSearchChange,
}) => {
  const [closedGroups, setClosedGroups] = useState(new Set<string>());

  const groupedPermissions = useMemo(() => {
    return permissions.reduce((acc, perm) => {
      const group = perm.group || "Chưa phân loại";
      if (!acc[group]) acc[group] = [];
      acc[group].push(perm);
      return acc;
    }, {} as PermissionGroup);
  }, [permissions]);

  const totalPermissions = permissions.length;
  const checkedPermissions = permissions.filter((p) => stagedPermissions.has(p.id)).length;

  const handleToggleGroupOpen = (groupName: string) => {
    setClosedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });
  };

  const handleToggleGroup = (groupName: string, permsInGroup: Permission[]) => {
    const allChecked = permsInGroup.every((perm) => stagedPermissions.has(perm.id));

    permsInGroup.forEach((perm) => {
      const isCurrentlyChecked = stagedPermissions.has(perm.id);
      if (allChecked && isCurrentlyChecked) {
        onTogglePermission(perm.id);
      } else if (!allChecked && !isCurrentlyChecked) {
        onTogglePermission(perm.id);
      }
    });
  };

  const renderContent = () => {
    if (!selectedItemName) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-700">Chọn bộ phận</h3>
          <p className="text-gray-500 mt-2">Vui lòng chọn một bộ phận từ danh sách bên trái để xem và chỉnh sửa quyền</p>
        </div>
      );
    }

    if (isLoadingAll || isLoadingItem) {
      return <PermissionSkeleton />;
    }

    if (Object.keys(groupedPermissions).length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-700">Không có quyền nào</h3>
          <p className="text-gray-500 mt-2">Chưa có quyền nào được cấu hình trong hệ thống</p>
        </div>
      );
    }

    return (
      <div className="p-6 space-y-4">
        {Object.entries(groupedPermissions).map(([groupName, permsInGroup]) => {
          const isOpen = !closedGroups.has(groupName);
          const checkedCount = permsInGroup.filter((perm) => stagedPermissions.has(perm.id)).length;
          const isGroupChecked = checkedCount === permsInGroup.length;
          const isGroupIndeterminate = checkedCount > 0 && checkedCount < permsInGroup.length;

          return (
            <div key={groupName} className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
              {/* Group Header */}
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
                <button
                  type="button"
                  className="flex items-center gap-3 flex-1 text-left focus:outline-none"
                  onClick={() => handleToggleGroupOpen(groupName)}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                    isGroupChecked ? "bg-emerald-500" : isGroupIndeterminate ? "bg-amber-500" : "bg-gray-200"
                  }`}>
                    <svg
                      className={`w-4 h-4 text-white transition-transform duration-200 ${isOpen ? "" : "-rotate-90"}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  <div>
                    <span className="font-bold text-gray-800">{groupName}</span>
                    <span className="ml-2 text-sm text-gray-500">
                      ({checkedCount}/{permsInGroup.length} đã chọn)
                    </span>
                  </div>
                </button>

                <input
                  type="checkbox"
                  checked={isGroupChecked}
                  ref={(el) => el && (el.indeterminate = isGroupIndeterminate)}
                  onChange={() => handleToggleGroup(groupName, permsInGroup)}
                  className="h-5 w-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
              </div>

              {/* Permissions List */}
              {isOpen && (
                <div className="p-4 space-y-3 bg-gray-50">
                  {permsInGroup.map((perm) => (
                    <PermissionItem
                      key={perm.id}
                      permission={perm}
                      isChecked={stagedPermissions.has(perm.id)}
                      onToggle={() => onTogglePermission(perm.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-5 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Title & Stats */}
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-200">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Phân quyền cho bộ phận</h2>
              <p className="text-sm text-gray-500">
                {selectedItemName ? (
                  <>
                    <span className="font-semibold text-emerald-600">{selectedItemName}</span>
                    <span className="mx-2">•</span>
                    <span>{checkedPermissions}/{totalPermissions} quyền đã cấp</span>
                  </>
                ) : (
                  "Chọn bộ phận để xem quyền"
                )}
              </p>
            </div>
          </div>

          {/* Search & Actions */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1 lg:w-80">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Tìm kiếm quyền..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
              />
            </div>
            <button
              onClick={onSave}
              disabled={!isDirty || !selectedItemName || isSaving}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all ${
                isDirty && selectedItemName
                  ? "bg-emerald-600 text-white hover:bg-emerald-700 shadow-lg shadow-emerald-200"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
              }`}
            >
              {isSaving ? (
                <>
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span>Đang lưu...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Lưu thay đổi</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        {selectedItemName && totalPermissions > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
              <span>Tiến độ cấp quyền</span>
              <span>{Math.round((checkedPermissions / totalPermissions) * 100)}%</span>
            </div>
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-500 to-green-500 rounded-full transition-all duration-300"
                style={{ width: `${(checkedPermissions / totalPermissions) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">{renderContent()}</div>
    </div>
  );
};
